import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';
import { mdiFileRemove } from '@mdi/js';

import {
    AddFilesToBudgetRequestDocument,
    IFileFragment,
    RemoveFilesFromBudgetRequestDocument,
} from 'graphql/types';
import { FileTable } from 'common/tables';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { IconButton } from 'common/components/IconButton';
import { BUDGET_REQUEST_FILE_MAX_SIZE } from 'common/constants/files';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';

interface IProps {
    budgetRequestId: string;
    files: IFileFragment[];
}

export const BudgetRequestFileTable = ({ budgetRequestId, files }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [filesAreUpdating, setFilesAreUpdating] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);

    // Collects the ids of files uploaded within a single batch before they are
    // committed to the budget request in one mutation.
    const uploadingFileRefs = useRef<string[]>([]);
    const deleteCallbackRef = useRef<() => Promise<boolean>>(undefined);

    const [addFiles] = useMutation(AddFilesToBudgetRequestDocument);
    const [removeFiles] = useMutation(RemoveFilesFromBudgetRequestDocument);

    const handleAddFile = useCallback((uploadedFile: IFileFragment) => {
        uploadingFileRefs.current.push(uploadedFile.id);
    }, []);

    const handleUploadFinish = useCallback(
        (filesUpdateCallback: (fileIds: string[]) => void) => {
            const fileIds = uploadingFileRefs.current;

            if (!fileIds.length) {
                filesUpdateCallback([]);

                setFilesAreUpdating(false);

                return;
            }

            // FileTable calls this from an effect, so the callback itself must
            // stay synchronous — run the mutation in a nested async function.
            const commitUploadedFiles = async () => {
                try {
                    const result = await addFiles({
                        variables: { id: budgetRequestId, fileIds },
                    });

                    const addResult = result.data?.addFilesToBudgetRequest;
                    const { succeededIds = [], failedIds = [] } =
                        addResult?.bulkAdd || {};

                    if (succeededIds.length) {
                        displaySnackbar(
                            translate('budgetRequestForm.files.addSuccess', {
                                count: succeededIds.length,
                            }),
                            { variant: 'success' }
                        );
                    }

                    if (failedIds.length) {
                        displaySnackbar(
                            translate('budgetRequestForm.files.addFailed', {
                                count: failedIds.length,
                            }),
                            { variant: 'error' }
                        );
                    }

                    // Files that failed to attach are passed along too, so they
                    // are dropped from the table instead of lingering as rows
                    // that look attached.
                    filesUpdateCallback([
                        ...(addResult?.budgetRequest.files.map(
                            (file) => file.id
                        ) || []),
                        ...failedIds,
                    ]);
                } catch (error) {
                    showApolloError(error);

                    // showApolloError stays silent for transport errors, so
                    // report the failed batch here as well.
                    displaySnackbar(
                        translate('budgetRequestForm.files.addFailed', {
                            count: fileIds.length,
                        }),
                        { variant: 'error' }
                    );

                    filesUpdateCallback(fileIds);
                } finally {
                    uploadingFileRefs.current = [];

                    setFilesAreUpdating(false);
                }
            };

            void commitUploadedFiles();
        },
        [addFiles, budgetRequestId, displaySnackbar, showApolloError, translate]
    );

    const handleRemoveFiles = useCallback(
        async (
            fileIds: string[],
            filesUpdateCallback: (fileIds: string[]) => void
        ): Promise<boolean> => {
            setFilesAreUpdating(true);

            try {
                const result = await removeFiles({
                    variables: { id: budgetRequestId, fileIds },
                    optimisticResponse: {
                        removeFilesFromBudgetRequest: {
                            __typename: 'RemoveFilesFromBudgetRequest',
                            budgetRequest: {
                                __typename: 'BudgetRequest',
                                id: budgetRequestId,
                                files: files.filter(
                                    (file) => !fileIds.includes(file.id)
                                ),
                            },
                            bulkRemove: {
                                __typename: 'BulkAction',
                                succeededIds: fileIds,
                                failedIds: [],
                            },
                        },
                    },
                });

                const removeResult = result.data?.removeFilesFromBudgetRequest;
                const { succeededIds = [], failedIds = [] } =
                    removeResult?.bulkRemove || {};

                if (succeededIds.length) {
                    displaySnackbar(
                        translate('budgetRequestForm.files.removeSuccess', {
                            count: succeededIds.length,
                        }),
                        { variant: 'success' }
                    );
                }

                if (failedIds.length) {
                    displaySnackbar(
                        translate('budgetRequestForm.files.removeFailed', {
                            count: failedIds.length,
                        }),
                        { variant: 'error' }
                    );
                }

                filesUpdateCallback(
                    removeResult?.budgetRequest.files.map((file) => file.id) ||
                        []
                );

                return true;
            } catch (error) {
                showApolloError(error);

                // showApolloError stays silent for transport errors, so report
                // the failed batch here as well.
                displaySnackbar(
                    translate('budgetRequestForm.files.removeFailed', {
                        count: fileIds.length,
                    }),
                    { variant: 'error' }
                );

                filesUpdateCallback([]);

                return false;
            } finally {
                setFilesAreUpdating(false);
            }
        },
        [
            budgetRequestId,
            displaySnackbar,
            files,
            removeFiles,
            showApolloError,
            translate,
        ]
    );

    const renderSelectedActions = (
        selected: string[],
        onSelectAll: () => void,
        filesUpdateCallback: (fileIds: string[]) => void
    ) => (
        <Tooltip title={translate('delete')}>
            <Box>
                <IconButton
                    aria-label={translate('delete')}
                    color="inherit"
                    disabled={!selected.length || filesAreUpdating}
                    iconPath={mdiFileRemove}
                    iconSize="2.4rem"
                    onClick={() => {
                        deleteCallbackRef.current = async () => {
                            const succeeded = await handleRemoveFiles(
                                selected,
                                filesUpdateCallback
                            );

                            if (succeeded) onSelectAll();

                            return succeeded;
                        };

                        setShowDeleteAlert(true);
                    }}
                />
            </Box>
        </Tooltip>
    );

    const handleConfirmDelete = async () => {
        const succeeded = await deleteCallbackRef.current?.();

        // Keep the dialog open when the mutation fails so the user can retry.
        if (!succeeded) return;

        deleteCallbackRef.current = undefined;

        setShowDeleteAlert(false);
    };

    return (
        <>
            <FileTable
                inDrawer
                emptyText={translate('budgetRequestForm.files.noFiles')}
                id="budgetRequestFiles"
                items={files}
                maxFileSize={BUDGET_REQUEST_FILE_MAX_SIZE}
                renderSelectedActions={renderSelectedActions}
                title="budgetRequestForm.files.title"
                onAdd={filesAreUpdating ? undefined : handleAddFile}
                onUploadFinish={handleUploadFinish}
                onUploadStart={() => setFilesAreUpdating(true)}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={filesAreUpdating}
                            variant="contained"
                            onClick={() => {
                                void handleConfirmDelete();
                            }}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={filesAreUpdating}
                            variant="contained"
                            onClick={() => setShowDeleteAlert(false)}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteFileMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteFileMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
